/**
 * useVodStorageUsage — how much of the plan's VOD storage the account has used.
 *
 * Read from the same server metric the Billing page uses
 * (`POST /metrics/subscription/{user}/storage/{package}`), which counts
 * uploaded videos, transcoded files and ad assets. Summing `stream.totalBytes`
 * client-side is not the same figure twice over: it misses those, and it only
 * sees the streams of the workspace the page is standing in, while the storage
 * allowance is one pool shared by every workspace. A banner built on the local
 * sum therefore went quiet in every workspace but the one holding the videos.
 *
 * `usedBytes` is module state because the Content Library draws the storage
 * indicator and the plan-limit banner at the same time, and they must agree.
 */
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import MetricsService from '@/services/MetricsService'
import UserService from '@/services/UserService'

const usedBytes = ref(0)

const definitionProp = (sub, prop) => sub?.definitionOverride?.[prop] ?? sub?.package?.definition?.[prop]

export function useVodStorageUsage() {
	const store = useStore()

	const subscriptions = computed(() => store.getters['User/subscriptions'] || [])

	// VOD storage limit (GB) — the cached value the app already exposes.
	const maxVodStorage = computed(() => Number(store.getters['User/maxVodStorage']) || 0)

	// The subscription that holds the VOD storage quota. On most plans VOD
	// storage is part of the main plan (not a separate category:'vod' sub), so
	// pick the enabled subscription whose storage allotment matches the VOD
	// storage limit — i.e. the same "Assigned" row the Billing page shows.
	const storageSubscription = computed(() => {
		const subs = subscriptions.value.filter((s) => s?.enabled && definitionProp(s, 'storage'))
		return subs.find((s) => Number(definitionProp(s, 'storage')) === maxVodStorage.value) || subs[0] || null
	})

	const maxBytes = computed(() => maxVodStorage.value * 1000 ** 3)
	const ratio = computed(() => (maxBytes.value ? usedBytes.value / maxBytes.value : 0))
	const isExceeded = computed(() => maxBytes.value > 0 && ratio.value >= 1)

	// MetricsService already folds concurrent calls for the same package into
	// one request, so both callers asking on mount costs a single round trip.
	const fetchUsage = async () => {
		const packageId = storageSubscription.value?.package?._id
		if (!packageId) {
			usedBytes.value = 0
			return
		}
		try {
			const res = await MetricsService.getSubscriptionStorage(
				UserService.getUserId(store.getters['User/userId']), packageId)
			usedBytes.value = res?.bytes || 0
		} catch (err) {
			console.error('VOD storage usage fetch failed:', err)
		}
	}

	return {
		usedBytes,
		maxBytes,
		maxVodStorage,
		ratio,
		isExceeded,
		storageSubscription,
		fetchUsage,
	}
}
